/**
 * @documental
 * @export useSimpleTableDataSource
 * @import
 * import { useSimpleTableDataSource } from "capitalrx-components-2.0/lib/general";
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getProperty } from "dot-prop";
import stringify from "json-stable-stringify";

import { EmptyArray } from "../../utilities/EmptyArray";
import { EmptyObject } from "../../utilities/EmptyObject";
import { Batchable } from "../Batchable";
import { DataId } from "../DataId";
import { DataRow } from "../DataRow";
import { DataSource } from "../DataSource";
import { FilterMethods, FilterSet, Filterable } from "../Filterable";
import { Indexable } from "../Indexable";
import { Paged } from "../Paged";
import { SortMethods, Sortable } from "../Sortable";
import { BaseDataSourceState } from "./BaseDataSourceState";
import { DataSourceSortEntry } from "./DataSourceSortEntry";
import { TableDataSourceOptions } from "./TableDataSourceOptions";
import { useSharedDataSourceFunctions } from "./useSharedDataSourceFunctions";

/**
 * The internal data source state.
 */

interface State<TRow extends object> extends BaseDataSourceState<TRow> {}

/**
 * The data source initial state.
 */

const getInitialState = <TRow extends object>({
  options,
  rows,
}: {
  options: TableDataSourceOptions | undefined;
  rows: readonly DataRow<TRow>[];
}) => ({
  rows: EmptyArray as ReadonlyArray<DataRow<TRow>>,
  page: 0,
  pageSize: options?.pageSize ?? 20,
  hasNextPage: false,
  totalRows: 0,
  maxRows: rows.length,
});

/**
 * Sorts the specified set of rows.
 * @param rows The rows to sort.
 * @param sort The sorting information.
 * @returns The sorted rows.
 */
function doSort<TRow extends object>(
  rows: ReadonlyArray<DataRow<TRow>>,
  sort: ReadonlyArray<DataSourceSortEntry>,
): ReadonlyArray<DataRow<TRow>> {
  return [...rows].sort(({ data: a }, { data: b }) => {
    for (const entry of sort) {
      const { name, direction } = entry;

      const valueA = getProperty<TRow, string, unknown | null>(a, name, null);
      const valueB = getProperty<TRow, string, unknown | null>(b, name, null);

      if (valueA === valueB) {
        // move to the next sort entry to break the tie.
        continue;
      }

      let result = 0;

      if (typeof valueA === "number" && typeof valueB === "number") {
        result = valueA - valueB;
      } else if (typeof valueA === "string" && typeof valueB === "string") {
        result = (valueA as string).localeCompare(valueB);
      } else {
        if (!valueA && valueB) result = -1;
        else if (valueA && !valueB) result = 1;
        else if (valueA && valueB) {
          if (valueA < valueB) result - 1;
          else if (valueA > valueB) result = 1;
        }
      }

      if (direction === "ascending") return result;
      else if (direction === "descending") return result * -1;

      return 0;
    }

    return 0;
  });
}

/**
 * Filters the specified set of rows.
 * @param rows The rows to filter.
 * @param filter The filter information.
 * @returns The filtered rows.
 */
function doFilter<TRow extends object>(rows: ReadonlyArray<DataRow<TRow>>, filter: FilterSet): ReadonlyArray<DataRow<TRow>> {
  function filterRow(data: TRow, filterKey: string, filterValue: unknown): boolean {
    const rowValue = getProperty<TRow, string, unknown | null>(data, filterKey, null);
    if (rowValue) {
      if (typeof rowValue === "string" && typeof filterValue === "string") {
        return (rowValue?.toLocaleLowerCase() as string).includes(filterValue?.toLocaleLowerCase());
      } else {
        return rowValue === filterValue;
      }
    }
    return false;
  }

  // do the filtering.
  for (const key of Object.keys(filter)) {
    const filterValue = filter[key];
    if (!filterValue) continue;

    if (key === "full_text_search") {
      rows = rows.filter((row) => {
        for (const rowKey of Object.keys(row.data)) {
          if (filterRow(row.data, rowKey, filterValue)) return true;
        }
        return false;
      });
    } else {
      rows = rows.filter((row) => filterRow(row.data, key, filterValue));
    }
  }

  return rows;
}

/**
 * Provides a table data source which wraps around a static array of row data.
 * @param rows The set of rows to create a data source for. This array must be immutable.
 * @returns A table data source which provides access to the row data.
 */
export function useSimpleTableDataSource<TRow extends object>(
  rows: ReadonlyArray<DataRow<TRow>>,
  options?: TableDataSourceOptions,
): DataSource<TRow> & Paged & Sortable & Filterable & Indexable<TRow> & Batchable<FilterMethods & SortMethods> {
  const sharedDataSourceFunctions = useSharedDataSourceFunctions({
    sort: options?.sort || (EmptyArray as ReadonlyArray<DataSourceSortEntry>),
    filter: options?.filter || (EmptyObject as FilterSet),
  });

  const initialState = getInitialState({ options, rows });

  const [internalState, setInternalState] = useState<State<TRow>>(initialState);

  const previousFilterRef = useRef<FilterSet>(sharedDataSourceFunctions.filter);

  const sortString = stringify(sharedDataSourceFunctions.sort);
  const filterString = stringify(sharedDataSourceFunctions.filter);

  useEffect(() => {
    setInternalState((current) => {
      const { pageSize } = current;
      const { sort, filter } = sharedDataSourceFunctions;

      let { page } = current;

      if (filterString !== stringify(previousFilterRef.current)) {
        page = 0;
        previousFilterRef.current = sharedDataSourceFunctions.filter;
      }

      let displayRows = doFilter(rows, filter);

      const totalRows = displayRows.length;

      displayRows = doSort(displayRows, sort);

      let startIndex = pageSize * page;
      if (startIndex > displayRows.length - 1) {
        const adjustedPage = Math.floor(displayRows.length / pageSize);

        page = adjustedPage;
        startIndex = pageSize * adjustedPage;
      }

      const hasNextPage = startIndex + pageSize < displayRows.length;

      displayRows = displayRows.slice(startIndex, startIndex + pageSize);

      return {
        ...current,
        totalRows,
        maxRows: totalRows,
        page,
        hasNextPage,
        rows: displayRows,
      };
    });
  }, [internalState.page, internalState.pageSize, sortString, filterString, rows]);

  const rowLookupTable = useMemo(() => {
    const entries = rows.map(({ id, data }) => [id, data]) as ReadonlyArray<[DataId, TRow]>;
    return new Map<DataId, TRow>(entries);
  }, [rows]);

  const displayLookupTable = useMemo(() => {
    const entries = internalState.rows.map(({ id, data }) => [id, data]) as ReadonlyArray<[DataId, TRow]>;
    return new Map<DataId, TRow>(entries);
  }, [internalState.rows]);

  const get = useCallback(
    async (id: DataId | ReadonlyArray<DataId>) => {
      if (Array.isArray(id)) {
        return id.map((value) => rowLookupTable.get(value) ?? null).filter((row) => row !== null);
      }
      return rowLookupTable.get(id as DataId) ?? null;
    },
    [rowLookupTable],
  );

  const lookup = useCallback(
    (id: DataId) => {
      return displayLookupTable.get(id) ?? null;
    },
    [displayLookupTable],
  );

  const setPageSize = useCallback((pageSize: number) => {
    setInternalState((current) => ({
      ...current,
      pageSize,
    }));
  }, []);

  const first = useCallback(() => {
    setInternalState((current) => ({ ...current, page: 0 }));
  }, []);

  const next = useCallback(() => {
    setInternalState((current) => ({
      ...current,
      page: !current.hasNextPage ? current.page : current.page + 1,
    }));
  }, []);

  const previous = useCallback(() => {
    setInternalState((current) => ({
      ...current,
      page: current.page === 0 ? 0 : current.page - 1,
    }));
  }, []);

  const last = useCallback(() => {
    setInternalState((current) => ({
      ...current,
      page: !current.hasNextPage ? current.page : Math.ceil(current.maxRows / current.pageSize) - 1,
    }));
  }, []);

  const reload = useCallback(async (reset: boolean = false) => {
    if (reset) {
      setInternalState(initialState);
    } else {
      // just refresh by regenning the internal rows
      setInternalState((current) => ({
        ...current,
        rows: [...current.rows],
      }));
    }
  }, []);

  return useMemo((): DataSource<TRow> & Paged & Sortable & Filterable & Indexable<TRow> & Batchable<FilterMethods & SortMethods> => {
    const { sort, filter, ...sharedRest } = sharedDataSourceFunctions;

    return {
      ...internalState,
      ...sharedRest,
      status: "success",
      isPending: false,
      enabled: true,
      busy: false,
      get: get as never,
      metadata: null,
      lookup,
      first,
      previous,
      next,
      last,
      setPageSize,
      reload,
    };
  }, [internalState, sharedDataSourceFunctions, lookup, get]);
}
